import { UsercentricsScript } from '@s-group/react-usercentrics';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { generateDefaultSeo, generateNextSeo } from 'next-seo/pages';
import type { PropsWithChildren } from 'react';

import { ScrollToTop } from '#components/Footer/ScrollToTop';
import { ContextProvider } from '#cutils/context/ContextProvider';
import { useMaintainScrollPosition } from '#cutils/hooks/useMaintainScrollPosition';
import type { AppShellPageProps } from '#cutils/page-context/server-side-props';
import { useApollo } from '#utils/apollo/client';

import { ErrorBoundary } from '../Error/ErrorBoundary';
import { ErrorServerError } from '../Error/ErrorServerError';
import { Footer } from '../Footer/Footer';
import { HTMLHead } from '../HTMLHead/HTMLHead';
import { Header } from '../Header/Header';

import styles from './AppShellLayout.module.scss';

export function AppShellLayout({
	pageProps,
	children,
	nofollow,
	className = '',
}: PropsWithChildren<Pick<AppProps<AppShellPageProps>, 'pageProps'> & { nofollow: boolean; className?: string }>) {
	const apolloClient = useApollo(pageProps);
	const baseUrl = pageProps.environment.constants.BASE_URL ?? '';
	const appUrl = pageProps.environment.constants.appUrl;

	useMaintainScrollPosition();

	return (
		<>
			{/* Injects static meta tags into the `<head />`. */}
			<HTMLHead baseUrl={baseUrl} appUrl={appUrl} />

			<Head>
				{/* add `noindex` on non-prod environments */}
				{nofollow && generateDefaultSeo({ dangerouslySetAllPagesToNoFollow: true, dangerouslySetAllPagesToNoIndex: true })}
				{generateNextSeo(pageProps.defaultSeoConfig)}
			</Head>

			<ContextProvider apolloClient={apolloClient} environment={pageProps.environment}>
				<Header navigation={pageProps.navigation} navigationTitle={pageProps.navigationTitle} />

				<main className={`${styles.content} ${className}`}>
					<ErrorBoundary fallback={<ErrorServerError />}>{children}</ErrorBoundary>
					<div className={styles.scrollTopWrapper}>
						<ScrollToTop className={styles.scrollTop} />
					</div>
				</main>
				<Footer bayernNavigation={pageProps.navigation.bayern} />
			</ContextProvider>

			{/* This is the ID for the live configuration, if you need access data to Usercentrics please contact Johanna Borrajoconde (Digitale Produktentwicklung) */}
			<UsercentricsScript
				settingsId={pageProps.environment.keys.USERCENTRICS_ID}
				draft={pageProps.environment.flags.USERCENTRICS_DRAFT_MODE ? true : undefined}
			/>
		</>
	);
}
